// Motor (§10.1): runs every tick after the tactic has filled `brain.want` and `brain.press`.
// Steers toward the wanted point or direction with context steering (16 probe directions scored by
// how well they serve the goal, minus edge, hazard and crowd penalties, with hysteresis), then
// low-pass filters the stick so the bean never jitters, adds a slow wander wobble on long walks,
// fidgets instead of idling, and turns presses into frame counters. Shoves get aim noise and the
// per-lunge blunder roll here (§10.6), so every shove a bot throws is imperfect in a human way.
import { NO_SLOT, TUNE } from '../constants';
import { BS, EK } from '../ids';
import { DEG, distToSegmentSq } from '../math';
import { chance, rand, randIntRange } from '../rng';
import type { Brain } from './brain';
import { gauss, type Ctx } from './context';
import { BOT_TUNE, byDifficulty } from './tuning';
import { datan2, dcos, dsin } from '../sim/dmath';

const M = BOT_TUNE.motor;
const DIRS = M.dirCount;
const DIR_X = new Float64Array(DIRS);
const DIR_Y = new Float64Array(DIRS);
for (let i = 0; i < DIRS; i++) {
  DIR_X[i] = dcos((i / DIRS) * Math.PI * 2);
  DIR_Y[i] = dsin((i / DIRS) * Math.PI * 2);
}

/** Score of one probe direction (higher is better). */
function scoreDir(ctx: Ctx, ux: number, uy: number, wx: number, wy: number, margin: number, ignoreDanger: boolean, contact: number, noCrowd = false): number {
  const b = ctx.self;
  const f = ctx.field;
  const interest = ux * wx + uy * wy;
  // Probe where this direction takes us: current drift plus a short and a long step.
  const vx = b.vel.x * M.velProbeSec;
  const vy = b.vel.y * M.velProbeSec;
  const nearS = f.safety(b.pos.x + vx + ux * M.probeNear, b.pos.y + vy + uy * M.probeNear);
  const farS = f.safety(b.pos.x + vx + ux * M.probeFar, b.pos.y + vy + uy * M.probeFar);
  const s = nearS < farS ? nearS : farS;
  let pen = 0;
  if (s < margin) {
    const k = (margin - s) / (margin > 0.25 ? margin : 0.25);
    pen += M.edgeWeight * k * k;
    if (nearS < M.edgeHardSafety) pen += M.edgeHardPenalty;
  }
  if (!ignoreDanger) {
    const dz = ctx.hz.danger(b.pos.x + ux * M.probeFar, b.pos.y + uy * M.probeFar, b.radius, true);
    if (dz > 0) pen += M.dangerWeight * dz * ctx.brain.p.hazardRespect;
  }
  if (f.isBlocked(b.pos.x + ux * M.probeNear, b.pos.y + uy * M.probeNear)) pen += M.blockedPenalty;
  // Banana peels on the floor (gathered by `steer`): walk around them (the whole stretch ahead).
  if (peelCount > 0) {
    const reach = TUNE.banana.triggerRadius + M.peelBerth;
    const ex = b.pos.x + vx + ux * M.probeFar;
    const ey = b.pos.y + vy + uy * M.probeFar;
    for (let i = 0; i < peelCount; i++) {
      if (distToSegmentSq(peelX[i], peelY[i], b.pos.x, b.pos.y, ex, ey) < reach * reach) {
        pen += peelWeight;
        break;
      }
    }
  }
  // Bumpers launch whoever touches them: steer well clear (with the current drift).
  if (!ignoreDanger && (f.isLaunch(b.pos.x + vx + ux * M.probeNear, b.pos.y + vy + uy * M.probeNear) || f.isLaunch(b.pos.x + vx + ux * M.probeFar, b.pos.y + vy + uy * M.probeFar))) {
    // A crown wearer has the most to lose to a launch over the rim (the crown goes with the KO).
    pen += M.launchPenalty * ctx.brain.p.hazardRespect * (ctx.self.holdsCrown ? 2 : 1);
  }
  if (noCrowd) return interest - pen;
  // Do not bowl through bystanders.
  const px = b.pos.x + ux * M.crowdProbe;
  const py = b.pos.y + uy * M.crowdProbe;
  for (let s2 = 0; s2 < ctx.seen.length; s2++) {
    if (s2 === b.slot || s2 === contact) continue;
    const o = ctx.seen[s2];
    if (!o.body) continue;
    const d = Math.hypot(o.x - px, o.y - py);
    if (d < M.crowdRadius) pen += M.crowdWeight * (1 - d / M.crowdRadius);
  }
  return interest - pen;
}

/** Fidget instead of standing still: short, slow, safe steps every second or two (§29.4: no idling). */
function fidget(ctx: Ctx, br: Brain, out: { x: number; y: number }): number {
  const w = ctx.w;
  const b = ctx.self;
  if (w.tick >= br.shuffleNext) {
    br.shuffleNext = w.tick + randIntRange(br, M.fidgetMinTicks, M.fidgetMaxTicks);
    br.shuffleUntil = w.tick + randIntRange(br, M.fidgetStepMinTicks, M.fidgetStepMaxTicks);
    // Pick a step that does not walk toward the edge.
    let bestS = -1e9;
    for (let k = 0; k < 4; k++) {
      const a = rand(br) * Math.PI * 2;
      const x = dcos(a);
      const y = dsin(a);
      if (ctx.field.isBlocked(b.pos.x + x * M.probeNear, b.pos.y + y * M.probeNear)) continue;
      const s = ctx.field.safety(b.pos.x + x * M.probeFar, b.pos.y + y * M.probeFar) + rand(br) * M.fidgetRandom;
      if (s > bestS) {
        bestS = s;
        br.shuffleX = x;
        br.shuffleY = y;
      }
    }
  }
  if (w.tick < br.shuffleUntil) {
    out.x = br.shuffleX;
    out.y = br.shuffleY;
    return M.fidgetSpeed;
  }
  out.x = 0;
  out.y = 0;
  return 0;
}

const fid = { x: 0, y: 0 };
/** Peels near the steering bot this tick (scratch for `scoreDir`). */
const peelX = new Float64Array(8);
const peelY = new Float64Array(8);
let peelCount = 0;
let peelWeight = 0;

/** Gathers the banana peels a step or two around the bot that it would slip on. */
function gatherPeels(ctx: Ctx): void {
  const b = ctx.self;
  peelCount = 0;
  peelWeight = byDifficulty(M.peelPenalty, ctx.brain.p.difficulty);
  if (b.z - b.groundZ > TUNE.bean.airborneZ) return;
  for (const e of ctx.w.ents) {
    if (e.kind !== EK.PEEL || peelCount >= peelX.length) continue;
    if (e.owner === b.slot && e.t < TUNE.banana.ownerImmuneTicks) continue;
    if (Math.hypot(e.pos.x - b.pos.x, e.pos.y - b.pos.y) > M.probeFar + Math.hypot(b.vel.x, b.vel.y) * M.velProbeSec + TUNE.banana.triggerRadius + M.peelBerth) continue;
    peelX[peelCount] = e.pos.x;
    peelY[peelCount] = e.pos.y;
    peelCount++;
  }
}

/** Turns the wanted motion into a smoothed stick. */
function steer(ctx: Ctx): void {
  const b = ctx.self;
  const br = ctx.brain;
  const wn = br.want;
  const f = ctx.frame;
  gatherPeels(ctx);
  let wx = 0;
  let wy = 0;
  let speed = wn.speed;
  let dist = 1e9;
  if (wn.mode === 1) {
    const dx = wn.px - b.pos.x;
    const dy = wn.py - b.pos.y;
    dist = Math.hypot(dx, dy);
    if (dist > 1e-6) {
      wx = dx / dist;
      wy = dy / dist;
    }
    if (dist < wn.arrive) speed *= dist / wn.arrive;
    // Below the input deadzone a stick does nothing: either really arrive or keep walking.
    if (dist < M.arriveStop) speed = 0;
    else if (speed > 0 && speed < M.minMove) speed = M.minMove;
  } else if (wn.mode === 2) {
    const l = Math.hypot(wn.px, wn.py);
    if (l > 1e-6) {
      wx = wn.px / l;
      wy = wn.py / l;
    } else speed = 0;
  } else speed = 0;

  if (br.unstickUntil > ctx.w.tick && b.state === BS.NORMAL) {
    const l = Math.max(1e-6, Math.hypot(br.unstickX, br.unstickY));
    wx = br.unstickX / l;
    wy = br.unstickY / l;
    speed = M.unstickSpeed;
    dist = 1e9;
  }

  let tx = 0;
  let ty = 0;
  if (speed < M.minSpeed) {
    if (!wn.precise && !br.waiting) {
      const s = fidget(ctx, br, fid);
      tx = fid.x * s;
      ty = fid.y * s;
    }
    // Standing near the edge with nothing to do is how beans get booped: drift inward.
    const here = ctx.field.safety(b.pos.x, b.pos.y);
    if (!wn.precise && b.state === BS.NORMAL && here < wn.margin * M.idleInwardFrac) {
      ctx.field.gradient(b.pos.x, b.pos.y, fid);
      const l = Math.hypot(fid.x, fid.y);
      if (l > 1e-6) {
        tx = (fid.x / l) * M.idleInwardSpeed;
        ty = (fid.y / l) * M.idleInwardSpeed;
      }
    }
  } else {
    br.shuffleNext = Math.max(br.shuffleNext, ctx.w.tick + M.fidgetMinTicks);
    // Context steering.
    const exact = scoreDir(ctx, wx, wy, wx, wy, wn.margin, wn.ignoreDanger, wn.contact, wn.noCrowd);
    let best = -1e9;
    let bi = -1;
    for (let i = 0; i < DIRS; i++) {
      const s = scoreDir(ctx, DIR_X[i], DIR_Y[i], wx, wy, wn.margin, wn.ignoreDanger, wn.contact, wn.noCrowd);
      if (s > best) {
        best = s;
        bi = i;
      }
    }
    let ux = wx;
    let uy = wy;
    if (exact < best - M.exactTolerance) {
      // Hysteresis: keep the previous detour direction unless clearly worse.
      if (br.dirIdx >= 0 && br.dirIdx !== bi) {
        const prev = scoreDir(ctx, DIR_X[br.dirIdx], DIR_Y[br.dirIdx], wx, wy, wn.margin, wn.ignoreDanger, wn.contact, wn.noCrowd);
        if (prev >= best - M.dirHysteresis) bi = br.dirIdx;
      }
      br.dirIdx = bi;
      ux = DIR_X[bi];
      uy = DIR_Y[bi];
      // A detour that does not serve the goal at all keeps full speed (we are escaping).
      if (ux * wx + uy * wy < 0) speed = Math.max(speed, M.escapeSpeed);
    } else br.dirIdx = -1;
    // Slow wander wobble on long walks.
    if (!wn.urgent && !wn.precise && dist > M.wobbleMinDist) {
      br.wobblePhase += br.wobbleRate;
      const a = dsin(br.wobblePhase) * M.wobbleDeg * DEG;
      const c = dcos(a);
      const s = dsin(a);
      const rx = ux * c - uy * s;
      const ry = ux * s + uy * c;
      ux = rx;
      uy = ry;
    }
    tx = ux * speed;
    ty = uy * speed;
  }

  // Unstick: pushing the stick but going nowhere (a wall, a cube, the cup) for a while means
  // walking around it for a moment, like a person would.
  const pushing = Math.hypot(tx, ty) > M.stuckStick ? 2 : Math.hypot(tx, ty) >= M.minMove ? 1 : 0;
  if (pushing > 0 && Math.hypot(b.vel.x, b.vel.y) < M.stuckSpeed && b.state === BS.NORMAL && b.z - b.groundZ < 0.05) br.stuckTicks += pushing;
  else br.stuckTicks = 0;
  if (br.stuckTicks > M.stuckTicks) {
    // Walk around it on the side with better footing (the detour still goes through steering).
    br.stuckTicks = 0;
    const l = Math.max(1e-6, Math.hypot(tx, ty));
    const px = -ty / l;
    const py = tx / l;
    const f = ctx.field;
    const sa = f.safety(b.pos.x + px, b.pos.y + py) - (f.isBlocked(b.pos.x + px * M.probeNear, b.pos.y + py * M.probeNear) ? 1 : 0);
    const sb = f.safety(b.pos.x - px, b.pos.y - py) - (f.isBlocked(b.pos.x - px * M.probeNear, b.pos.y - py * M.probeNear) ? 1 : 0);
    const side = sa === sb ? (rand(br) < 0.5 ? 1 : -1) : sa > sb ? 1 : -1;
    br.unstickX = px * side;
    br.unstickY = py * side;
    br.unstickUntil = ctx.w.tick + M.unstickTicks;
  }

  const alpha = wn.urgent ? M.urgentSmoothing : br.p.stickSmoothing;
  br.sx += (tx - br.sx) * alpha;
  br.sy += (ty - br.sy) * alpha;
  // Smooth the direction, not the speed: a moving stick keeps the wanted magnitude unless it is
  // swinging through the middle (a reversal), where a human's stick also passes near zero.
  const m = Math.hypot(br.sx, br.sy);
  const mag = Math.hypot(tx, ty);
  if (mag > M.minSpeed && m > M.keepMagnitudeMin) {
    const k = Math.min(mag, 1) / m;
    br.sx *= k;
    br.sy *= k;
  } else if (m > 1) {
    br.sx /= m;
    br.sy /= m;
  }
  if (Math.abs(br.sx) < 1e-4 && Math.abs(br.sy) < 1e-4) {
    br.sx = 0;
    br.sy = 0;
  }
  f.mx = br.sx;
  f.my = br.sy;
}

/** Applies the shove's aim noise and blunder roll (§10.6); returns the final direction in `out`. */
function noisyAim(ctx: Ctx, x: number, y: number, out: { x: number; y: number }): void {
  const br = ctx.brain;
  let a = datan2(y, x) + gauss(br) * br.p.aimNoise;
  if (chance(br, br.p.blunderChance)) {
    a += (chance(br, 0.5) ? 1 : -1) * TUNE.bot.blunderAngleDeg * DEG;
    ctx.tele.blunders++;
  }
  out.x = dcos(a);
  out.y = dsin(a);
}

const aim = { x: 0, y: 0 };

/** Turns `brain.press` into counter increments and held bits on the frame. */
function presses(ctx: Ctx): void {
  const br = ctx.brain;
  const pr = br.press;
  const f = ctx.frame;
  const b = ctx.self;
  if (pr.shove) {
    let x = pr.sx;
    let y = pr.sy;
    if (x === 0 && y === 0) {
      x = dcos(b.facing);
      y = dsin(b.facing);
    }
    if (pr.noisy) noisyAim(ctx, x, y, aim);
    else {
      const l = Math.hypot(x, y);
      aim.x = x / l;
      aim.y = y / l;
    }
    f.sx = aim.x;
    f.sy = aim.y;
    // Face the shove this tick (facing snaps to the stick in NORMAL), so the assist always applies.
    if (b.state === BS.NORMAL) {
      f.mx = aim.x;
      f.my = aim.y;
      br.sx = aim.x * M.postShoveStick;
      br.sy = aim.y * M.postShoveStick;
    }
    br.cShove = (br.cShove + 1) & 15;
    br.heldShove = M.heldTicks;
    br.lastShoveTick = ctx.w.tick;
    ctx.tele.shoves++;
  }
  if (pr.grab) {
    if (pr.sx !== 0 || pr.sy !== 0) {
      noisyAimGrab(ctx, pr.sx, pr.sy);
      if (b.state === BS.NORMAL) {
        f.mx = aim.x;
        f.my = aim.y;
      }
      f.sx = aim.x;
      f.sy = aim.y;
    }
    if (pr.ax !== 0 || pr.ay !== 0) {
      f.ax = pr.ax;
      f.ay = pr.ay;
    }
    br.cGrab = (br.cGrab + 1) & 15;
    br.heldGrab = M.heldTicks;
  }
  if (pr.jump) {
    br.cJump = (br.cJump + 1) & 15;
    br.heldJump = M.heldTicks;
  }
  if (pr.item) {
    if (pr.ax !== 0 || pr.ay !== 0) {
      f.ax = pr.ax;
      f.ay = pr.ay;
    }
    br.cItem = (br.cItem + 1) & 15;
    br.heldItem = M.heldTicks;
  }
  let held = 0;
  if (br.heldShove > 0) {
    held |= 1;
    br.heldShove--;
  }
  if (br.heldGrab > 0) {
    held |= 2;
    br.heldGrab--;
  }
  if (br.heldJump > 0) {
    held |= 4;
    br.heldJump--;
  }
  if (br.heldItem > 0) {
    held |= 8;
    br.heldItem--;
  }
  f.held = held;
  pr.shove = pr.grab = pr.jump = pr.item = false;
  pr.sx = pr.sy = pr.ax = pr.ay = 0;
  pr.noisy = false;
}

function noisyAimGrab(ctx: Ctx, x: number, y: number): void {
  const br = ctx.brain;
  const a = datan2(y, x) + gauss(br) * br.p.aimNoise * M.grabNoiseMult;
  aim.x = dcos(a);
  aim.y = dsin(a);
}

/** Motor entry point: stick, then presses (a press may override the stick to face its aim). */
export function runMotor(ctx: Ctx): void {
  steer(ctx);
  presses(ctx);
}

/** Resets the want to "stand still" with the bot's default margin; tactics overwrite it. */
export function clearWant(ctx: Ctx): void {
  const wn = ctx.brain.want;
  wn.mode = 0;
  wn.speed = 0;
  wn.margin = ctx.margin;
  wn.arrive = M.arriveDist;
  wn.urgent = false;
  wn.precise = false;
  wn.contact = NO_SLOT;
  wn.ignoreDanger = false;
  wn.noCrowd = false;
}

export function wantPoint(ctx: Ctx, x: number, y: number, speed: number): void {
  const wn = ctx.brain.want;
  wn.mode = 1;
  wn.px = x;
  wn.py = y;
  wn.speed = speed;
}

export function wantDir(ctx: Ctx, x: number, y: number, speed: number): void {
  const wn = ctx.brain.want;
  wn.mode = 2;
  wn.px = x;
  wn.py = y;
  wn.speed = speed;
}
